// src/pages/Devices.js
import React, { useEffect, useState } from "react";
// ✅ Import specific functions from the updated deviceApi.js
import { fetchDevices, addDevice, deleteDevice } from "../utils/deviceApi";

// Helper to format timestamp (optional, if you add last_update later)
const formatTime = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp * 1000);
    return !isNaN(date) ? date.toLocaleString() : "Invalid Date";
};

export default function Devices() {
    const [devices, setDevices] = useState([]); // Initialize state as an empty array
    // Use separate states for form fields for better control
    const [newDeviceId, setNewDeviceId] = useState("");
    const [newDeviceName, setNewDeviceName] = useState("");
    const [newDeviceLocation, setNewDeviceLocation] = useState("");
    const [loading, setLoading] = useState(false); // For Add button state
    const [isFetching, setIsFetching] = useState(true); // Track initial load

    // --- Data Fetching ---
    const loadDevices = async () => {
        setIsFetching(true); // Indicate loading start
        const data = await fetchDevices();
        // ✅ Ensure data is always an array before setting state
        setDevices(Array.isArray(data) ? data : []);
        setIsFetching(false); // Indicate loading finished
    };

    // --- Initial Load & Auto-Refresh ---
    useEffect(() => {
        loadDevices(); // Load devices when component mounts
        const interval = setInterval(loadDevices, 8000); // Refresh every 8 seconds
        return () => clearInterval(interval); // Cleanup interval on unmount
    }, []); // Empty dependency array means run only once on mount

    // --- Event Handlers ---
    const handleAddDevice = async (e) => {
        e.preventDefault(); // Prevent default form submission
        if (!newDeviceId.trim()) return alert("Device ID is required!");
        // Use default values if name/location are empty
        const nameToAdd = newDeviceName.trim() || `Device ${newDeviceId}`;
        const locationToAdd = newDeviceLocation.trim() || "Default Location";

        setLoading(true);
        const result = await addDevice({
            device_id: newDeviceId,
            name: nameToAdd,
            location: locationToAdd,
        });
        setLoading(false);

        if (result && result.devices) {
            alert(`✅ Device "${newDeviceId}" added successfully!`);
            // Update the local state directly with the response from the backend
            setDevices(Array.isArray(result.devices) ? result.devices : []);
            // Clear form
            setNewDeviceId("");
            setNewDeviceName("");
            setNewDeviceLocation("");
        } else if (result && result.error) {
             alert(`⚠️ ${result.error}`); // Show specific error from backend
        }
        // No explicit fetchDevices() needed here if backend returns updated list
    };

    const handleDeleteDevice = async (deviceId) => {
        if (!window.confirm(`Are you sure you want to delete device "${deviceId}"?`)) return;

        const result = await deleteDevice(deviceId);
        if (result && result.devices) {
             alert(`🗑️ ${result.message}`);
             // Update local state with the response from the backend
             setDevices(Array.isArray(result.devices) ? result.devices : []);
        }
         // No explicit fetchDevices() needed here
    };

    // --- Render ---
    return (
        <div className="min-h-screen bg-darkBg text-white p-6">
            {/* Header */}
            <header className="text-center mb-8">
                <h1 className="text-4xl font-bold text-neonPink drop-shadow-md">🧩 Device Management</h1>
                <p className="text-gray-400 mt-2">Manage all Raspberry Pi devices connected to PiWatch.</p>
            </header>

            {/* Add New Device Form */}
            <form onSubmit={handleAddDevice} className="glass-card p-6 mb-8 glow-border rounded-xl">
                <h2 className="text-2xl font-bold text-neonBlue mb-4">➕ Add New Device</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <input
                        type="text"
                        placeholder="Device ID*"
                        value={newDeviceId}
                        onChange={(e) => setNewDeviceId(e.target.value)}
                        required // HTML5 required attribute
                        className="p-3 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-neonBlue"
                    />
                    <input
                        type="text"
                        placeholder="Device Name (Optional)"
                        value={newDeviceName}
                        onChange={(e) => setNewDeviceName(e.target.value)}
                        className="p-3 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-neonBlue"
                    />
                    <input
                        type="text"
                        placeholder="Location (Optional)"
                        value={newDeviceLocation}
                        onChange={(e) => setNewDeviceLocation(e.target.value)}
                        className="p-3 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-neonBlue"
                    />
                </div>
                <button
                    type="submit" // Use type="submit" for forms
                    disabled={loading}
                    className={`mt-4 bg-neonBlue text-black px-6 py-2 rounded-lg font-semibold hover:bg-neonPink transition ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                    {loading ? "Adding..." : "Add Device"}
                </button>
            </form>

            {/* Device List Table */}
            <div className="glass-card p-6 glow-border rounded-xl">
                <h2 className="text-2xl font-bold text-neonBlue mb-4">📋 Registered Devices</h2>
                {isFetching ? (
                    <p className="text-center text-gray-400 py-6">Loading devices...</p>
                 ) : !Array.isArray(devices) || devices.length === 0 ? ( // Extra safety check
                    <p className="text-gray-400 text-center py-6">No devices found in database.</p>
                 ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-800 text-neonPink">
                                    <th className="p-3">Device ID</th>
                                    <th className="p-3">Name</th>
                                    <th className="p-3">Location</th>
                                    <th className="p-3 text-center">Status</th>
                                    {/* Optional: Add Last Seen later if backend provides it */}
                                    {/* <th className="p-3">Last Seen</th> */}
                                    <th className="p-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map((d, i) => (
                                    <tr key={d.device_id || i} className="hover:bg-gray-800/50 transition-colors duration-200">
                                        <td className="p-3">{d.device_id}</td>
                                        <td className="p-3">{d.name || 'N/A'}</td>
                                        <td className="p-3">{d.location || 'N/A'}</td>
                                        <td className="p-3 text-center">
                                            <span className={`inline-block w-3 h-3 rounded-full ${ d.active ? "bg-green-400 animate-pulse" : "bg-red-500" } mr-2`}></span>
                                            {d.active ? "Active" : "Inactive"}
                                        </td>
                                        {/* Optional: Add Last Seen later */}
                                        {/* <td className="p-3 text-gray-400">{formatTime(d.last_update)}</td> */}
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => handleDeleteDevice(d.device_id)}
                                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-semibold transition-colors"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                 )}
            </div>
        </div>
    );
}